import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Pencil, Trash2, Plus, RefreshCw, UserPlus, UserMinus, QrCode } from "lucide-react";
import { toast } from "sonner";
import { UnitQRModal } from "./UnitQRModal";
import { BulkBar, RowCheck } from "./BulkBar";

export function AdminUnitsTab({ buildings = [] }) {
  const [buildingId, setBuildingId] = useState("");
  const [units, setUnits] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ number: "", label: "" });
  const [saving, setSaving] = useState(false);

  const [assignUnit, setAssignUnit] = useState(null);
  const [assignUser, setAssignUser] = useState("");

  const [toDelete, setToDelete] = useState(null);
  const [qrUnit, setQrUnit] = useState(null);

  // --- bulk selection ----------------------------------------------------
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDelete, setBulkDelete] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => { setSelected(new Set()); }, [buildingId]);

  const load = useCallback(async () => {
    if (!buildingId) { setUnits([]); setResidents([]); return; }
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        api.get("/units", { params: { building_id: buildingId } }),
        api.get("/platform/users", { params: { role: "resident", building_id: buildingId } }),
      ]);
      setUnits(u.data);
      setResidents(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load units");
    } finally { setLoading(false); }
  }, [buildingId]);
  useEffect(() => { load(); }, [load]);

  function openNew() {
    setForm({ number: "", label: "" });
    setEditing("new");
  }
  function openEdit(u) {
    setForm({ number: u.number || "", label: u.label || "" });
    setEditing(u);
  }

  async function save() {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/units", { building_id: buildingId, number: form.number, label: form.label || null });
        toast.success("Unit created");
      } else {
        await api.patch(`/buildings/${buildingId}/units/${editing.id}`, { number: form.number, label: form.label || null });
        toast.success("Unit updated");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function doDelete() {
    try {
      await api.delete(`/buildings/${buildingId}/units/${toDelete.id}`);
      toast.success("Unit deleted");
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  }

  async function doAssign() {
    try {
      await api.post("/units/assign", { user_id: assignUser, unit_id: assignUnit.id });
      toast.success("Resident assigned");
      setAssignUnit(null); setAssignUser("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assign failed");
    }
  }

  async function unassign(unitId, userId) {
    try {
      await api.post("/units/unassign", { user_id: userId, unit_id: unitId });
      toast.success("Resident unassigned");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unassign failed");
    }
  }

  // --- bulk helpers ------------------------------------------------------
  function toggleRow(id, on) {
    setSelected((prev) => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; });
  }
  function toggleAllOnPage(on) {
    setSelected(() => (on ? new Set(units.map((u) => u.id)) : new Set()));
  }
  function clearSelection() { setSelected(new Set()); }
  async function runBulkDelete() {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/platform/bulk/units/delete", { ids: Array.from(selected) });
      const skippedN = data.skipped?.length || 0;
      toast.success(`${data.deleted} deleted${skippedN ? ` · ${skippedN} skipped` : ""}`);
      setBulkDelete(false);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkBusy(false); }
  }
  const allOnPageChecked = units.length > 0 && units.every((u) => selected.has(u.id));

  const unassignedResidents = residents.filter((r) => !r.unit_id);

  return (
    <div className="space-y-4" data-testid="admin-units-tab">
      <div className="card-stroke p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[260px]">
          <Label className="label-tiny">Building</Label>
          <Select value={buildingId || "none"} onValueChange={(v)=>setBuildingId(v==="none"?"":v)}>
            <SelectTrigger className="mt-2" data-testid="units-filter-building">
              <SelectValue placeholder="Pick a building"/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Pick a building</SelectItem>
              {buildings.map((b)=>(<SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} disabled={!buildingId} data-testid="units-refresh">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`}/> Refresh
        </Button>
        <Button onClick={openNew} disabled={!buildingId} data-testid="units-new">
          <Plus size={14} className="mr-1.5"/> New unit
        </Button>
      </div>

      {!buildingId ? (
        <div className="card-stroke p-8 text-center text-sm text-muted-foreground" data-testid="units-pick-building">
          Choose a building to view, create or edit its units.
        </div>
      ) : (
        <div className="card-stroke p-0 overflow-x-auto">
          <BulkBar count={selected.size} onClear={clearSelection} testId="units-bulk-bar">
            <Button size="sm" variant="outline"
                    onClick={() => setBulkDelete(true)}
                    className="text-destructive hover:text-destructive"
                    data-testid="units-bulk-delete">
              <Trash2 size={14} className="mr-1.5"/> Delete
            </Button>
          </BulkBar>
          <table className="w-full text-sm" data-testid="units-table">
            <thead>
              <tr className="label-tiny text-left border-b border-border">
                <th className="py-3 pl-4 pr-2 w-10">
                  <RowCheck checked={allOnPageChecked} onChange={toggleAllOnPage}
                            testId="units-select-all" label="Select all units"/>
                </th>
                <th>Number</th><th>Label</th><th>Residents</th>
                <th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {units.length === 0 ? (
                <tr><td colSpan={5} className="py-8 text-center text-muted-foreground" data-testid="units-empty">
                  {loading ? "Loading…" : "No units yet in this building."}
                </td></tr>
              ) : units.map((u) => {
                const unitResidents = residents.filter((r) => (u.resident_ids || []).includes(r.id));
                return (
                  <tr key={u.id} className="border-b border-border last:border-0" data-testid={`unit-row-${u.id}`}>
                    <td className="py-3 pl-4 pr-2">
                      <RowCheck checked={selected.has(u.id)} onChange={(on)=>toggleRow(u.id, on)}
                                testId={`unit-select-${u.id}`}/>
                    </td>
                    <td className="font-mono">{u.number}</td>
                    <td className="text-xs text-muted-foreground">{u.label || "—"}</td>
                    <td>
                      {unitResidents.length === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {unitResidents.map((r) => (
                            <span key={r.id}
                                  className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-secondary text-secondary-foreground rounded-full"
                                  data-testid={`unit-${u.id}-resident-${r.id}`}>
                              {r.name}
                              <button onClick={()=>unassign(u.id, r.id)} className="ml-1 hover:text-destructive"
                                      aria-label="Unassign"
                                      data-testid={`unit-${u.id}-unassign-${r.id}`}>
                                <UserMinus size={10}/>
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="text-right pr-4 whitespace-nowrap">
                      <Button size="icon" variant="ghost" onClick={()=>setQrUnit(u)}
                              data-testid={`unit-qr-${u.id}`} aria-label="Show QR"><QrCode size={14}/></Button>
                      <Button size="icon" variant="ghost" onClick={()=>{ setAssignUnit(u); setAssignUser(""); }}
                              data-testid={`unit-assign-${u.id}`} aria-label="Assign resident"><UserPlus size={14}/></Button>
                      <Button size="icon" variant="ghost" onClick={()=>openEdit(u)}
                              data-testid={`unit-edit-${u.id}`} aria-label="Edit"><Pencil size={14}/></Button>
                      <Button size="icon" variant="ghost" onClick={()=>setToDelete(u)}
                              data-testid={`unit-delete-${u.id}`} aria-label="Delete">
                        <Trash2 size={14} className="text-destructive"/>
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o)=>{ if (!o) setEditing(null); }}>
        <DialogContent data-testid="unit-modal">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Create unit" : `Edit unit ${editing?.number}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="label-tiny">Number</Label>
              <Input value={form.number} onChange={(e)=>setForm({...form, number: e.target.value})} className="mt-2 font-mono" data-testid="unit-form-number"/></div>
            <div><Label className="label-tiny">Label (optional)</Label>
              <Input value={form.label} onChange={(e)=>setForm({...form, label: e.target.value})} className="mt-2" data-testid="unit-form-label"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditing(null)} data-testid="unit-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving || !form.number} data-testid="unit-form-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignUnit} onOpenChange={(o)=>{ if (!o) { setAssignUnit(null); setAssignUser(""); } }}>
        <DialogContent data-testid="unit-assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign resident to unit {assignUnit?.number}</DialogTitle>
          </DialogHeader>
          {unassignedResidents.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No unassigned residents in this building. Create a resident account first or unassign one from another unit.
            </div>
          ) : (
            <div>
              <Label className="label-tiny">Resident</Label>
              <Select value={assignUser} onValueChange={setAssignUser}>
                <SelectTrigger className="mt-2" data-testid="unit-assign-user"><SelectValue placeholder="Choose a resident"/></SelectTrigger>
                <SelectContent>
                  {unassignedResidents.map((r)=>(
                    <SelectItem key={r.id} value={r.id}>{r.name} · {r.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>{ setAssignUnit(null); setAssignUser(""); }} data-testid="unit-assign-cancel">Cancel</Button>
            <Button onClick={doAssign} disabled={!assignUser} data-testid="unit-assign-submit">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toDelete} onOpenChange={(o)=>{ if (!o) setToDelete(null); }}>
        <DialogContent data-testid="unit-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete unit {toDelete?.number}?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            Residents of this unit will be unassigned. This cannot be undone.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setToDelete(null)} data-testid="unit-delete-cancel">Cancel</Button>
            <Button onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid="unit-delete-confirm">
              Delete unit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnitQRModal
        open={!!qrUnit}
        unitId={qrUnit?.id}
        onOpenChange={(o)=>{ if (!o) setQrUnit(null); }}
      />

      <Dialog open={bulkDelete} onOpenChange={setBulkDelete}>
        <DialogContent data-testid="units-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete {selected.size} unit{selected.size === 1 ? "" : "s"}?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            Residents of these units will be unassigned. This cannot be undone.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setBulkDelete(false)} data-testid="units-bulk-delete-cancel">Cancel</Button>
            <Button onClick={runBulkDelete} disabled={bulkBusy}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid="units-bulk-delete-confirm">
              {bulkBusy ? "Deleting…" : `Delete ${selected.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
